import React, { useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import SearchBox from "../../components/Search";
import HighlightedText from "../../components/HighlightKeyword";
import { CircularProgress } from "@mui/material";
import {
  TiArrowSortedDown,
  TiArrowSortedUp,
  TiArrowUnsorted,
} from "react-icons/ti";
import Pagination from "../../components/Pagination";

export default function FormsCausePapers() {
  const { stateId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [totalResult, setTotalResult] = useState(0);

  const location = useLocation();
  const allowMeta = false;
  const [query, setQuery] = useState("");
  const [documentList, setDocumentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const navigate = useNavigate();
  const limitParam = Number(searchParams.get("limit")) || 10;
  const pageParam = Number(searchParams.get("page")) || 0;
  const [limit, setLimit] = useState(limitParam ? Number(limitParam) : 10);
  const [currentPage, setCurrentPage] = useState(
    pageParam ? Number(pageParam) - 1 : 0
  );
  const [itemOffset, setItemOffset] = useState(
    Math.max((pageParam - 1) * limit, 0)
  );
  const [pageCount, setPageCount] = useState(0);
  const [currentItems, setCurrentItems] = useState([]);

  // Check if the path is /states
  const isStatesList = location.pathname.endsWith("/states");
  const states = [
    { id: 1, name: "Terengganu" },
    { id: 2, name: "Perlis" },
    { id: 3, name: "Selangor" },
    { id: 4, name: "Negeri Sembilan" },
    { id: 5, name: "Kelantan" },
    { id: 6, name: "Perak" },
    { id: 7, name: "Kedah" },
    { id: 8, name: "Pahang" },
    { id: 9, name: "Johor" },
    { id: 10, name: "Sabah" },
    { id: 11, name: "Melaka" },
    { id: 12, name: "Pulau Pinang" },
    { id: 13, name: "Sarawak" },
    { id: 14, name: "Federal" },
  ];

  const handleSearch = () => {};
  const renderBreadcrumbs = () => {
    if (!isStatesList) {
      const state = states.find((s) => s.id === Number(stateId));
      return (
        <div style={{ paddingTop: "25px", paddingLeft: "25px" }}>
          Law Library &gt; Forms and Cause Papers &gt; {state?.name}
        </div>
      );
    } else {
      return (
        <div style={{ paddingTop: "25px", paddingLeft: "25px" }}>
          Law Library &gt; Forms and Cause Papers &gt; States
        </div>
      );
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedList = [...documentList].sort((a, b) => {
      const aValue = a[key]?.toLowerCase() || "";
      const bValue = b[key]?.toLowerCase() || "";

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setDocumentList(sortedList);
    setSortConfig({ key, direction });
    setItemOffset(0);
    setCurrentPage(0);
  };

  const getArrowIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? (
        <TiArrowSortedUp size={18} className="case-law-arrow-up" />
      ) : (
        <TiArrowSortedDown size={18} className="case-law-arrow-down" />
      );
    } else {
      return <TiArrowUnsorted size={18} />;
    }
  };

  const handlePageClick = (event) => {
    const selectedPage = event.selected; // UI shows 1,2,3...
    setCurrentPage(selectedPage);

    const newOffset = (selectedPage * limit) % documentList.length;
    setItemOffset(newOffset);

    // update URL with zero-based page
    const params = new URLSearchParams(location.search);
    params.set("page", selectedPage + 1);
    params.set("limit", limit);
    navigate(`${location.pathname}?${params.toString()}`);

    // smooth scroll
    const syariahBody = document.querySelector(".law-library-search-body");
    if (syariahBody) {
      const bodyTop = syariahBody.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: bodyTop - 75, behavior: "smooth" });
    }
  };

  return (
    <div>
      {isStatesList ? (
        <div className="law-library-search-body">
          <table className="content case-law-table">
            <thead>
              <tr>
                <th style={{ minWidth: "5vw" }}>
                  <div className="case-law-table-header">State</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {states.map((state, index) => (
                <tr
                  key={index}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    navigate(`/law-library/formsCausePapers/${state.id}`);
                  }}
                >
                  <td>{state.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="law-library-search-body">
            {renderBreadcrumbs()}
            <div style={{ padding: "0 25px" }}>
              <SearchBox
                onSearch={handleSearch}
                placeholder={"Enter keyword to search"}
                query={query}
                limit={limit}
                setLimit={setLimit}
              />
            </div>
            {documentList.length > 0 ? (
              <div>
                {loading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 40,
                    }}
                  >
                    <CircularProgress />
                  </div>
                ) : (
                  <table className="content case-law-table">
                    <thead>
                      <tr>
                        <th
                          style={{ minWidth: "5vw" }}
                          onClick={() => handleSort("year")}
                        >
                          <div className="case-law-table-header">
                            Year {getArrowIcon("year")}
                          </div>
                        </th>
                        <th
                          style={{ minWidth: "10vw" }}
                          onClick={() => handleSort("no")}
                        >
                          <div className="case-law-table-header">
                            Act No {getArrowIcon("no")}
                          </div>
                        </th>
                        <th
                          style={{ minWidth: "15vw" }}
                          onClick={() => handleSort("title")}
                        >
                          <div className="case-law-table-header">
                            Title {getArrowIcon("title")}
                          </div>
                        </th>
                        <th
                          style={{ minWidth: "15vw" }}
                          onClick={() => handleSort("publishDate")}
                        >
                          <div className="case-law-table-header">
                            Published Date {getArrowIcon("publishDate")}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((result, index) => (
                        <tr key={index}>
                          <td>{result.year}</td>
                          <td className="egazette-file-title">
                            <p
                              onClick={() => {
                                navigate(
                                  `/law-library/document?documentID=${result.documentID}`
                                );
                              }}
                            >
                              {result.no}
                            </p>
                          </td>
                          <td className="egazette-file-title">
                            <p>
                              <HighlightedText
                                paragraph={result.title}
                                keyword={query}
                                onClick={() =>
                                  navigate(
                                    `/law-library/document?documentID=${result.documentID}`
                                  )
                                }
                              />
                            </p>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {result.publishDate
                              ? new Date(result.publishDate).toLocaleDateString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  }
                                )
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : (
              <div style={{ margin: "25px" }}>No results found</div>
            )}
          </div>

          <Pagination
            handlePageClick={handlePageClick}
            pageCount={pageCount}
            currentPageNumber={currentPage}
            totalResult={totalResult}
            limit={limit}
            offset={itemOffset}
          />
        </>
      )}
    </div>
  );
}
